import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { crm, money } from "../lib/crmClient";

export function CrmInvoices() {
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        crm.get("/crm/invoices").then((r) => setRows(r.data)).finally(() => setLoading(false));
    }, []);

    const filtered = rows.filter((r) => filter === "all" ? true : filter === "quotes" ? r.kind === "quote" : r.kind === "invoice");

    return (
        <div data-testid="crm-invoices">
            <div className="flex items-baseline justify-between flex-wrap gap-4">
                <div>
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">[ Invoices & Quotes ]</div>
                    <h1 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight mt-2">All jobs.</h1>
                </div>
                <Link to="/crm/invoices/new" data-testid="invoices-new-cta"
                    className="border border-[#FFE600] text-[#FFE600] hover:bg-[#FFE600] hover:text-black transition-colors px-5 py-2.5 font-mono text-[11px] tracking-[0.28em] uppercase">
                    + New
                </Link>
            </div>

            <div className="mt-8 flex gap-2">
                {[["all","All"],["quotes","Quotes"],["invoices","Invoices"]].map(([k,l]) => (
                    <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k)}
                        className={`px-4 py-2 font-mono text-[10px] tracking-[0.28em] uppercase border transition-colors ${filter===k?"border-[#FFE600] text-[#FFE600] bg-[#FFE600]/[0.06]":"border-white/20 text-white/60 hover:text-white"}`}>{l}</button>
                ))}
            </div>

            <div className="mt-6 border border-white/10">
                {loading ? (
                    <div className="p-8 font-mono text-[11px] tracking-[0.28em] uppercase text-white/40">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="p-10 text-center font-mono text-[11px] tracking-[0.28em] uppercase text-white/40">
                        Nothing here yet
                    </div>
                ) : (
                    <table className="w-full">
                        <thead>
                            <tr className="text-left border-b border-white/10">
                                {["Number","Title","Total","Status","Sent"].map((h) => (
                                    <th key={h} className="p-4 font-mono text-[9px] tracking-[0.28em] uppercase text-white/40">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => (
                                <tr key={r.id} data-testid={`invoice-row-${r.id}`} className="border-b border-white/5 hover:bg-white/[0.03]">
                                    <td className="p-4">
                                        <Link to={`/crm/invoices/${r.id}`} className="font-mono text-[12px] text-[#FFE600]">{r.number}</Link>
                                    </td>
                                    <td className="p-4 font-display uppercase text-lg">{r.title}</td>
                                    <td className="p-4 font-mono text-[13px]">{money(r.total)}</td>
                                    <td className="p-4">
                                        <span className="font-mono text-[10px] tracking-[0.24em] uppercase border border-white/25 px-2 py-1">{r.status}</span>
                                    </td>
                                    <td className="p-4 font-mono text-[11px] text-white/50">{new Date(r.created_at).toLocaleDateString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export function CrmInvoiceNew() {
    const nav = useNavigate();
    const [clients, setClients] = useState([]);
    const [f, setF] = useState({
        client_id: "", title: "", kind: "invoice",
        tax_rate: 0.07, deposit_percent: 30, notes: "",
        line_items: [{ description: "", quantity: 1, unit_price: 0 }],
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => { crm.get("/crm/clients").then((r) => setClients(r.data)); }, []);

    const setLI = (i, k, v) => {
        const li = [...f.line_items];
        li[i][k] = k === "description" ? v : parseFloat(v || 0);
        setF({ ...f, line_items: li });
    };
    const addLI = () => setF({ ...f, line_items: [...f.line_items, { description: "", quantity: 1, unit_price: 0 }] });
    const rmLI = (i) => setF({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) });

    const subtotal = f.line_items.reduce((s, x) => s + (x.quantity || 0) * (x.unit_price || 0), 0);
    const tax = subtotal * f.tax_rate;
    const total = subtotal + tax;
    const deposit = total * (f.deposit_percent / 100);

    const save = async (e) => {
        e.preventDefault();
        setError("");
        setSaving(true);
        try {
            const { data } = await crm.post("/crm/invoices", f);
            nav(`/crm/invoices/${data.id}`);
        } catch (err) {
            const d = err?.response?.data?.detail;
            setError(typeof d === "string" ? d : "Could not save — check the fields.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={save} data-testid="crm-invoice-new" className="max-w-4xl">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">[ New ]</div>
            <h1 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight mt-2">Create.</h1>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-6 gap-3">
                <select data-testid="inv-client" required value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })}
                    className="md:col-span-3 bg-black/40 border border-white/20 focus:border-[#FFE600] focus:outline-none px-3 py-2.5 font-mono text-[13px] text-white">
                    <option value="">— Select client —</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select data-testid="inv-kind" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}
                    className="md:col-span-1 bg-black/40 border border-white/20 focus:border-[#FFE600] focus:outline-none px-3 py-2.5 font-mono text-[13px] text-white">
                    <option value="invoice">Invoice</option>
                    <option value="quote">Quote</option>
                </select>
                <input data-testid="inv-title" required placeholder="Title (e.g. Exterior repaint)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })}
                    className="md:col-span-6 bg-black/40 border border-white/20 focus:border-[#FFE600] focus:outline-none px-3 py-2.5 font-mono text-[13px] text-white placeholder-white/30"/>
            </div>

            <div className="mt-6 border border-white/10">
                <div className="grid grid-cols-12 border-b border-white/10 bg-white/[0.02] font-mono text-[9px] tracking-[0.28em] uppercase text-white/40">
                    <div className="col-span-6 p-3">Description</div>
                    <div className="col-span-2 p-3">Qty</div>
                    <div className="col-span-3 p-3">Unit price</div>
                    <div className="col-span-1"></div>
                </div>
                {f.line_items.map((li, i) => (
                    <div key={i} data-testid={`li-row-${i}`} className="grid grid-cols-12 border-b border-white/5 items-center">
                        <input required data-testid={`li-desc-${i}`} placeholder="Prep + prime + 2 coats"
                            value={li.description} onChange={(e) => setLI(i, "description", e.target.value)}
                            className="col-span-6 bg-transparent px-3 py-2.5 font-mono text-[13px] focus:outline-none focus:bg-white/[0.03]"/>
                        <input required type="number" step="0.01" min="0.01" data-testid={`li-qty-${i}`}
                            value={li.quantity} onChange={(e) => setLI(i, "quantity", e.target.value)}
                            className="col-span-2 bg-transparent px-3 py-2.5 font-mono text-[13px] focus:outline-none focus:bg-white/[0.03]"/>
                        <input required type="number" step="0.01" min="0" data-testid={`li-price-${i}`}
                            value={li.unit_price} onChange={(e) => setLI(i, "unit_price", e.target.value)}
                            className="col-span-3 bg-transparent px-3 py-2.5 font-mono text-[13px] focus:outline-none focus:bg-white/[0.03]"/>
                        <button type="button" onClick={() => rmLI(i)} disabled={f.line_items.length===1}
                            className="col-span-1 font-mono text-[10px] tracking-[0.24em] uppercase text-red-400 hover:text-red-300 disabled:opacity-30">×</button>
                    </div>
                ))}
                <button type="button" onClick={addLI} data-testid="li-add"
                    className="w-full p-3 font-mono text-[10px] tracking-[0.28em] uppercase text-white/60 hover:text-white bg-white/[0.02] hover:bg-white/[0.04]">
                    + Add line item
                </button>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="glass border border-white/10 p-4">
                    <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-white/40">Tax rate</div>
                    <div className="flex items-baseline gap-2 mt-2">
                        <input type="number" step="0.001" min="0" max="1" value={f.tax_rate} onChange={(e) => setF({ ...f, tax_rate: parseFloat(e.target.value || 0) })}
                            className="w-24 bg-transparent border-b border-white/20 focus:border-[#FFE600] focus:outline-none px-1 font-mono text-[14px]"/>
                        <span className="font-mono text-[11px] text-white/50">({(f.tax_rate*100).toFixed(2)}%)</span>
                    </div>
                </label>
                <label className="glass border border-white/10 p-4">
                    <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-white/40">Deposit %</div>
                    <div className="flex items-baseline gap-2 mt-2">
                        <input type="number" step="1" min="0" max="100" value={f.deposit_percent} onChange={(e) => setF({ ...f, deposit_percent: parseInt(e.target.value || 0, 10) })}
                            className="w-24 bg-transparent border-b border-white/20 focus:border-[#FFE600] focus:outline-none px-1 font-mono text-[14px]"/>
                        <span className="font-mono text-[11px] text-white/50">= {money(deposit)}</span>
                    </div>
                </label>
                <div className="glass border border-[#FFE600]/40 p-4">
                    <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-[#FFE600]">Total</div>
                    <div className="font-display text-4xl mt-2">{money(total)}</div>
                    <div className="font-mono text-[10px] text-white/50 mt-1">Sub {money(subtotal)} · Tax {money(tax)}</div>
                </div>
            </div>

            <textarea data-testid="inv-notes" placeholder="Notes for the client (optional)" rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
                className="w-full mt-6 bg-black/40 border border-white/20 focus:border-[#FFE600] focus:outline-none px-3 py-2.5 font-mono text-[13px] text-white placeholder-white/30 resize-none"/>

            {error && <div data-testid="inv-error" className="mt-4 font-mono text-[12px] text-red-400">{error}</div>}

            <div className="mt-8 flex gap-3">
                <button type="submit" data-testid="inv-save" disabled={saving}
                    className="border border-[#FFE600] text-[#FFE600] hover:bg-[#FFE600] hover:text-black transition-colors px-6 py-3 font-mono text-[11px] tracking-[0.3em] uppercase disabled:opacity-50">
                    {saving ? "Saving…" : "Save →"}
                </button>
                <Link to="/crm/invoices" className="border border-white/25 hover:border-white transition-colors px-6 py-3 font-mono text-[11px] tracking-[0.3em] uppercase">
                    Cancel
                </Link>
            </div>
        </form>
    );
}
